import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { EntityAvatarFragment$key } from "./__generated__/EntityAvatarFragment.graphql";
import EntityProfilePic, { AvailableSizesKeys } from "./EntityProfilePic";
import { type ReactNode } from "react";
import { EntityAvatarQuery as EntityAvatarQueryType } from "./__generated__/EntityAvatarQuery.graphql";
import { Skeleton } from "@/kit/ui/skeleton";
import { generatePath, Link } from "react-router";

const EntityAvatarFragment = graphql`
  fragment EntityAvatarFragment on Entity {
    username
    displayName
    ...EntityProfilePicFragment @arguments(thumbnail: true)
  }
`;

const EntityAvatarQuery = graphql`
  query EntityAvatarQuery($id: ID!) {
    node(id: $id) {
      ... on Entity {
        ...EntityAvatarFragment
      }
    }
  }
`;

export interface Props {
  entity: EntityAvatarFragment$key;
  size?: AvailableSizesKeys;
  label?: ReactNode | null;
}

export function EntityAvatar({ entity: entityFragment, label, size }: Props) {
  const entity = useFragment(EntityAvatarFragment, entityFragment);
  return (
    <Link
      to={generatePath("/:username", { username: entity.username })}
      className="flex flex-row items-center justify-start gap-1.5"
    >
      <EntityProfilePic entity={entity} size={size ?? "10"} />
      <div className="flex flex-col">
        <span>{entity.username}</span>
        <span className="text-xs text-grey-700 font-normal break-all">
          {label ?? `@${entity.displayName}`}
        </span>
      </div>
    </Link>
  );
}

interface LazyEntityAvatarProps extends Omit<Props, "entity"> {
  id: ID;
}

function EntityAvatarLazy({ id, ...props }: LazyEntityAvatarProps) {
  const { node: entity } = useLazyLoadQuery<EntityAvatarQueryType>(
    EntityAvatarQuery,
    { id },
    { fetchPolicy: "store-or-network" },
  );
  if (!entity) return null;
  return <EntityAvatar entity={entity} {...props} />;
}

function EntityAvatarSkeleton({ withLabel = false }: { withLabel?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Skeleton className="rounded-full w-10 h-10" />
      <div className="flex flex-col">
        <Skeleton className="h-4 w-24 mb-1" />
        {withLabel && <Skeleton className="h-3 w-20" />}
      </div>
    </div>
  );
}

EntityAvatar.Skeleton = EntityAvatarSkeleton;
EntityAvatar.Lazy = EntityAvatarLazy;
export default EntityAvatar;
